import React, { useState, useMemo } from "react";
import Image from "next/image";
import { Popup } from "antd-mobile";
import type { TProductInfo } from "@/typings";
import { useProductSkus } from "@/api/product/use-query";
import { IconCloseBlue } from "@/components/Icons";
import ProductInfoSkeleton from "./skeleton";
import { RIGHT_ARROW } from "@/cdn";
import ProductSku from "./_components/ProductSku";
import ProductCommission from "./_components/ProductCommission";
import "./index.scss";

const classPrefix = "yfm-product-detail-info";

interface ProductInfoProps {
  productInfo: TProductInfo | null;
}

const ProductInfo: React.FC<ProductInfoProps> = ({ productInfo }) => {
  const [isShowFullProductName, setIsShowFullProductName] = useState(true);
  const [skuModal, setSkuModal] = useState(false);

  const {
    data: productSkuResponse,
    mutate: mutateProductSkus,
    status: skuStatus,
  } = useProductSkus();

  const productNameArrowStyle = useMemo(() => {
    return {
      rotate: isShowFullProductName ? "90deg" : "270deg",
    };
  }, [isShowFullProductName]);

  const lastCategoryName = useMemo(() => {
    if (productInfo?.categories) {
      const len = productInfo.categories?.length;
      return productInfo.categories[len - 1]?.name ?? "--";
    }
    return "--";
  }, [productInfo]);

  const productInfoList = useMemo(() => {
    return [
      {
        label: "Price",
        text:
          productInfo?.promotion_lower_price ===
          productInfo?.promotion_upper_price ? (
            (productInfo?.promotion_lower_price_str ?? "--")
          ) : (
            <>
              {productInfo?.promotion_lower_price_str ?? "--"} -<br />
              {productInfo?.promotion_upper_price_str ?? "--"}
            </>
          ),
      },
      {
        label: "Stock",
        text: productInfo?.stocks ?? "--",
      },
      {
        label: "Ratings",
        text: productInfo?.rating ?? "--",
      },
      {
        label: "Sample",
        text: productInfo?.available_samples ?? "--",
      },
    ];
  }, [productInfo]);

  const productSku = useMemo(() => {
    if (productSkuResponse?.code === 0) {
      return productSkuResponse.data;
    }
    return {
      props: null,
      skus: null,
    };
  }, [productSkuResponse]);

  const onSkuClick = () => {
    if (skuStatus === "success") {
      setSkuModal(true);
    } else {
      mutateProductSkus(productInfo?.product_id as string, {
        onSuccess() {
          setSkuModal(true);
        },
      });
    }
  };

  if (!productInfo) return <ProductInfoSkeleton />;

  return (
    <div className={classPrefix}>
      <Popup
        visible={skuModal}
        onMaskClick={() => setSkuModal(false)}
        onClose={() => setSkuModal(false)}
        showCloseButton
        closeIcon={<IconCloseBlue />}
        bodyClassName="yfm-popup-body"
      >
        <ProductSku
          cover={productInfo?.cover ?? ""}
          price={productInfo?.promotion_lower_price_str ?? ""}
          originalPrice={productInfo?.original_lower_price_str ?? ""}
          skuProps={productSku?.props ?? []}
          skus={productSku?.skus ?? {}}
        />
      </Popup>
      <div
        className={`${classPrefix}__product-name-box`}
        onClick={() => setIsShowFullProductName(!isShowFullProductName)}
      >
        <span
          className={`${classPrefix}__product-name ${
            isShowFullProductName ? "short" : ""
          }`}
        >
          {productInfo?.product_name ?? "--"}
        </span>
        <Image
          style={productNameArrowStyle}
          width={8}
          height={12}
          src={RIGHT_ARROW}
          alt=""
        />
      </div>

      <div className={`${classPrefix}__type-box`}>
        <span className={`${classPrefix}__type-category`}>
          {lastCategoryName}
        </span>
        <div className={`${classPrefix}__type-sku`} onClick={onSkuClick}>
          <span>SKU</span>
          <Image width={6} height={10} src={RIGHT_ARROW} alt="" />
        </div>
      </div>

      <div className={`${classPrefix}__earn-box`}>
        <div className={`${classPrefix}__earn-title`}>Earn:</div>
        <div className={`${classPrefix}__earn-amount`}>
          {productInfo?.est_creator_commission_amount_str ?? "--"}
        </div>
      </div>

      <div className={`${classPrefix}__product-info-box`}>
        {productInfoList.map((item) => (
          <dl key={item.label}>
            <dt>{item.label}</dt>
            <dd>{item.text}</dd>
          </dl>
        ))}
      </div>
      <ProductCommission productInfo={productInfo} />
    </div>
  );
};

export default ProductInfo;
